import { CircleHelp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useActiveNavItem } from '@/navigation/use-active-nav';
import { getSearchShortcutLabel } from '@/features/search';
import { Button } from '@/shared/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { resolveContextualHelp } from './contextual-help-copy';
import { HEADER_CHIP_ICON_ONLY, HEADER_CHIP_ICON_WELL } from './header-chrome';

/**
 * Route-aware tips — icon-only trigger.
 */
export function ContextualHelp(): JSX.Element {
  const [open, setOpen] = useState(false);
  const activeNav = useActiveNavItem();
  const shortcut = useMemo(() => getSearchShortcutLabel(), []);
  const copy = useMemo(() => resolveContextualHelp(activeNav?.label), [activeNav?.label]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={HEADER_CHIP_ICON_ONLY}
          aria-label={`Tips for ${copy.title}`}
          title="Tips"
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className={HEADER_CHIP_ICON_WELL}>
            <CircleHelp className="size-3.5" aria-hidden />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" sideOffset={8}>
        <div className="border-b hairline bg-gradient-to-br from-brand-50/70 via-white to-surface-50 px-3 py-2.5">
          <p className="text-[13px] font-semibold text-ink-900">{copy.title}</p>
          <p className="text-[11px] text-ink-500">Quick tips for this area</p>
        </div>
        <ul className="space-y-2 px-3 py-3">
          {copy.tips.map((tip) => (
            <li key={tip} className="text-[12px] leading-relaxed text-ink-600">
              {tip}
            </li>
          ))}
        </ul>
        <div className="space-y-1 border-t hairline p-1.5">
          <p className="px-2 py-1 text-[11px] text-ink-500">Keyboard: {shortcut} search</p>
          <Button variant="ghost" size="sm" className="h-8 w-full justify-start" asChild>
            <Link to="/help-support" onClick={() => setOpen(false)}>
              Help centre
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
